// Configuração centralizada da API
import { getApiBaseUrl } from './production';
import { SessionManager } from '@/utils/sessionManager';

// Função para obter a URL base da API com fallback
const getApiBaseUrlLegacy = (): string => {
  // Primeiro tenta pegar da variável de ambiente
  const envUrl = import.meta.env.URL_BASE_BACKEND;
  if (envUrl) {
    return envUrl;
  }

  // Fallback baseado no ambiente
  if (import.meta.env.DEV) {
    return 'http://localhost:3001';
  }

  // Produção - usar URL relativa ou configurar conforme necessário
  return window.location.origin;
};

export const API_CONFIG = {
  BASE_URL: getApiBaseUrl(), // Usando a nova função de produção
  ENDPOINTS: {
    EVENTS: '/api/events',
    PHOTOS: '/api/photos',
    ORDERS: '/api/orders',
  }
} as const;

// Função helper para construir URLs completas
export const buildApiUrl = (endpoint: string): string => {
  const baseUrl = API_CONFIG.BASE_URL;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

// Função helper para fazer fetch com configuração padrão
export const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = buildApiUrl(endpoint);
  
  // Incluir token de autenticação automaticamente
  const token = SessionManager.getToken();
  
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
    credentials: 'include', // Incluir cookies para autenticação
    ...options,
  };
  
  try {
    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      // Se for 401, NÃO limpar tokens automaticamente
      // Deixar o componente decidir se deve limpar a sessão
      if (response.status === 401) {
        console.warn('⚠️ API retornou 401 - Token pode estar inválido, mas não limpando sessão automaticamente');
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    
    return response;
  } catch (error) {
    console.error(`Erro na requisição para ${url}:`, error);
    throw error;
  }
};

// Cliente API simples usando axios-like interface
export const api = {
  get: async (endpoint: string) => {
    const response = await apiRequest(endpoint, { method: 'GET' });
    const data = await response.json();
    return { data };
  },
  
  post: async (endpoint: string, body?: any) => {
    const response = await apiRequest(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json();
    return { data };
  },
  
  put: async (endpoint: string, body?: any) => {
    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json();
    return { data };
  },
  
  delete: async (endpoint: string) => {
    const response = await apiRequest(endpoint, { method: 'DELETE' });
    const data = await response.json();
    return { data };
  },
  
  patch: async (endpoint: string, body?: any) => {
    const response = await apiRequest(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json();
    return { data };
  },
};